/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Public Dynamic Theme Schemas & DTOs (Task 10.1.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Individual color token map for a specific color mode (light / dark).
 * Values are HSL format components (e.g. "262 83% 58%") or CSS variables.
 */
export const ThemeColorTokensSchema = z
  .object({
    primary: z.string().openapi({ description: 'Primary brand hue HSL value', example: '262 83% 58%' }),
    primaryHover: z.string().openapi({ description: 'Primary hover state HSL', example: '262 83% 52%' }),
    primaryFg: z.string().openapi({ description: 'Primary foreground text HSL', example: '0 0% 100%' }),
    primary50: z.string().optional().openapi({ description: 'Primary subtle background 50', example: '262 100% 97%' }),
    primary100: z.string().optional().openapi({ description: 'Primary subtle border 100', example: '262 93% 94%' }),
    accent: z.string().openapi({ description: 'Accent cyan/emerald hue HSL', example: '199 89% 48%' }),
    accentHover: z.string().openapi({ description: 'Accent hover state HSL', example: '199 89% 42%' }),
    accentFg: z.string().openapi({ description: 'Accent foreground text HSL', example: '0 0% 100%' }),
    surface: z.string().openapi({ description: 'Card and panel surface background HSL', example: '0 0% 100%' }),
    surfaceElevated: z.string().openapi({ description: 'Elevated popover/modal surface HSL', example: '0 0% 98%' }),
    border: z.string().openapi({ description: 'Subtle border color HSL', example: '220 13% 91%' }),
    bgBase: z.string().openapi({ description: 'Main viewport background color HSL', example: '210 40% 98%' }),
    textMain: z.string().openapi({ description: 'Main heading and body text HSL', example: '222 47% 11%' }),
    textMuted: z.string().openapi({ description: 'Muted descriptive text HSL', example: '215 16% 47%' }),
    muted: z.string().openapi({ description: 'Muted chip and disabled background HSL', example: '210 40% 96%' }),
  })
  .openapi('ThemeColorTokens');

export type ThemeColorTokens = z.infer<typeof ThemeColorTokensSchema>;

/**
 * Platform branding assets and metadata schema.
 */
export const ThemeBrandingSchema = z
  .object({
    siteName: z.string().openapi({ description: 'Platform display name', example: 'Sophia Platform' }),
    slogan: z.string().optional().openapi({ description: 'Platform slogan / tagline', example: 'Next-Gen Edge-Native Generative AI Studio' }),
    logoLightUrl: z.string().openapi({ description: 'URL for light mode logo', example: '/assets/images/logo-light.svg' }),
    logoDarkUrl: z.string().openapi({ description: 'URL for dark mode logo', example: '/assets/images/logo-dark.svg' }),
    logoCollapsedUrl: z.string().openapi({ description: 'URL for collapsed sidebar mark icon', example: '/assets/images/logo-icon.svg' }),
    faviconUrl: z.string().openapi({ description: 'URL for platform favicon', example: '/favicon.ico' }),
  })
  .openapi('ThemeBranding');

export type ThemeBranding = z.infer<typeof ThemeBrandingSchema>;

/**
 * Complete Theme Response returned by GET /api/v1/public/theme.
 */
export const PublicThemeResponseSchema = z
  .object({
    themeId: z.string().openapi({ description: 'Unique theme identifier', example: 'sophia-edge-default' }),
    name: z.string().openapi({ description: 'Friendly theme name', example: 'Sophia Edge Electric Purple' }),
    tokens: z.object({
      light: ThemeColorTokensSchema,
      dark: ThemeColorTokensSchema,
    }).openapi({ description: 'Color token sets for light and dark modes' }),
    branding: ThemeBrandingSchema,
    updatedAt: z.string().openapi({ description: 'ISO 8601 timestamp of last theme update', example: '2026-10-24T00:00:00Z' }),
  })
  .openapi('PublicThemeResponse');

export type PublicThemeResponse = z.infer<typeof PublicThemeResponseSchema>;

/**
 * Admin Theme item in Global Theme Studio catalog.
 */
export const AdminThemeItemSchema = z
  .object({
    id: z.string().openapi({ example: 'sophia-edge-default', description: 'Unique theme identifier' }),
    name: z.string().min(1).openapi({ example: 'Sophia Edge Electric Purple', description: 'Human-readable theme name' }),
    description: z.string().optional().openapi({ example: 'Default brand theme with electric purple and cyan' }),
    isPreset: z.boolean().openapi({ example: true, description: 'True for built-in preset themes' }),
    isActive: z.boolean().openapi({ example: true, description: 'True if this theme is currently active platform-wide' }),
    tokens: z.object({
      light: ThemeColorTokensSchema,
      dark: ThemeColorTokensSchema,
    }).openapi({ description: 'Color token sets for light and dark modes' }),
    createdAt: z.string().optional().openapi({ example: '2026-10-26T14:00:00Z' }),
    updatedAt: z.string().optional().openapi({ example: '2026-10-26T14:00:00Z' }),
  })
  .openapi('AdminThemeItem');

export type AdminThemeItem = z.infer<typeof AdminThemeItemSchema>;

export const AdminThemeListSchema = z.array(AdminThemeItemSchema).openapi('AdminThemeList');

/**
 * Schema for creating a new custom theme.
 */
export const CreateThemeInputSchema = z
  .object({
    name: z.string().min(1, 'Theme name is required').openapi({ example: 'Aurora Borealis' }),
    description: z.string().optional().openapi({ example: 'Custom northern lights palette' }),
    tokens: z.object({
      light: ThemeColorTokensSchema,
      dark: ThemeColorTokensSchema,
    }).openapi({ description: 'Full light and dark token specifications' }),
  })
  .openapi('CreateThemeInput');

export type CreateThemeInput = z.infer<typeof CreateThemeInputSchema>;

/**
 * Schema for updating an existing theme.
 */
export const UpdateThemeInputSchema = z
  .object({
    name: z.string().min(1).optional().openapi({ example: 'Updated Theme Name' }),
    description: z.string().optional(),
    tokens: z
      .object({
        light: ThemeColorTokensSchema.partial().optional(),
        dark: ThemeColorTokensSchema.partial().optional(),
      })
      .optional()
      .openapi({ description: 'Partial token overrides' }),
  })
  .openapi('UpdateThemeInput');

export type UpdateThemeInput = z.infer<typeof UpdateThemeInputSchema>;
